import type { PendingAction } from '@nutians/shared/schemas';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PendingActionCardProps {
  action: PendingAction;
  onConfirm: (decision: 'accept' | 'reject') => Promise<void>;
  readOnly?: boolean;
}

type ActionState = 'pending' | 'accepting' | 'rejecting' | 'accepted' | 'rejected' | 'error';

export function PendingActionCard({ action, onConfirm, readOnly = false }: PendingActionCardProps) {
  const { t } = useTranslation('coach');
  const [state, setState] = useState<ActionState>('pending');

  async function handleDecision(decision: 'accept' | 'reject') {
    setState(decision === 'accept' ? 'accepting' : 'rejecting');
    try {
      await onConfirm(decision);
      setState(decision === 'accept' ? 'accepted' : 'rejected');
    } catch {
      setState('error');
    }
  }

  const toolLabel = t(`tool.${action.payload.tool}`, { defaultValue: action.payload.tool });

  return (
    <aside
      className="mt-3 rounded-xl border border-husk bg-shell dark:border-ink/20 dark:bg-leaf"
      aria-label={`Proposed action: ${action.summary}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-husk px-4 py-2.5 dark:border-husk">
        <span
          className="rounded-full bg-husk px-2 py-0.5 text-[11px] font-medium
                     text-ink-soft dark:bg-shell dark:text-ink-soft"
        >
          {toolLabel}
        </span>
        <span className="text-sm text-ink dark:text-ink">{action.summary}</span>
      </div>

      {/* Payload preview */}
      <div className="px-4 py-3">
        {action.payload.tool === 'log_entry' && (
          <blockquote
            className="border-l-2 border-ink/20 pl-3 text-sm text-ink-soft
                       dark:border-ink/20 dark:text-ink-soft italic"
          >
            {action.payload.text}
          </blockquote>
        )}

        {action.payload.tool === 'update_target' && (
          <div className="space-y-2">
            <dl className="grid grid-cols-5 gap-x-3 text-sm">
              {(
                [
                  ['kcal', action.payload.kcal, 'kcal'],
                  ['protein', action.payload.proteinG, 'g'],
                  ['carbs', action.payload.carbsG, 'g'],
                  ['fat', action.payload.fatG, 'g'],
                  ['fiber', action.payload.fiberG, 'g'],
                ] as [string, number, string][]
              ).map(([label, value, unit]) => (
                <div key={label} className="flex flex-col items-center">
                  <dt className="text-[10px] uppercase tracking-wide text-ink-soft dark:text-ink-soft">
                    {label}
                  </dt>
                  <dd className="font-medium text-ink dark:text-ink">
                    {value}
                    <span className="text-xs text-ink-soft">{unit}</span>
                  </dd>
                </div>
              ))}
            </dl>
            {action.payload.rationale && (
              <p className="text-xs text-ink-soft dark:text-ink-soft italic">
                {action.payload.rationale}
              </p>
            )}
          </div>
        )}

        {action.payload.tool === 'request_export' && (
          <p className="text-sm text-ink-soft dark:text-ink-soft">
            Scope: <span className="font-medium">{action.payload.scope}</span>
            {action.payload.from && (
              <span>
                {' '}
                · from <span className="font-medium">{action.payload.from}</span>
              </span>
            )}
            {action.payload.to && (
              <span>
                {' '}
                · to <span className="font-medium">{action.payload.to}</span>
              </span>
            )}
          </p>
        )}
      </div>

      {/* Decision buttons */}
      <div className="flex items-center gap-2 border-t border-husk px-4 py-2.5 dark:border-husk">
        {state === 'accepted' && (
          <p className="text-sm font-medium text-leaf dark:text-leaf">{t('action.done')}</p>
        )}
        {state === 'rejected' && (
          <p className="text-sm text-ink-soft dark:text-ink-soft">{t('action.dismissed')}</p>
        )}
        {state === 'error' && (
          <p className="text-sm text-clay dark:text-clay" role="alert">
            {t('action.error')}
          </p>
        )}
        {readOnly && state === 'pending' && (
          <p className="text-sm text-ink-soft dark:text-ink-soft">{t('action.expired')}</p>
        )}
        {!readOnly && (state === 'pending' || state === 'accepting' || state === 'rejecting') && (
          <>
            <button
              type="button"
              onClick={() => handleDecision('accept')}
              disabled={state !== 'pending'}
              aria-label={`Accept: ${action.summary}`}
              className="rounded-lg px-3 py-1.5 text-sm font-medium min-h-[36px]
                         bg-leaf text-white hover:bg-shell
                         disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-leaf
                         dark:bg-husk dark:text-ink dark:hover:bg-leaf-deep"
            >
              {state === 'accepting' ? t('action.accepting') : t('action.accept')}
            </button>
            <button
              type="button"
              onClick={() => handleDecision('reject')}
              disabled={state !== 'pending'}
              aria-label={`Reject: ${action.summary}`}
              className="rounded-lg px-3 py-1.5 text-sm font-medium min-h-[36px]
                         text-ink-soft hover:text-ink
                         disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-leaf
                         dark:text-ink-soft dark:hover:text-ink"
            >
              {state === 'rejecting' ? t('action.dismissing') : t('action.dismiss')}
            </button>
            <span
              className="ml-auto rounded-full bg-leaf/10 px-2 py-0.5 text-[10px]
                         font-medium text-leaf dark:text-leaf"
            >
              {t('action.free')}
            </span>
          </>
        )}
      </div>
    </aside>
  );
}
